import _ from 'lodash';

export const defaultNormalizedState = { ids: [], entities: {} };

export const normalizeById = (entities, key = 'id') => {
  if (!(entities && entities.length)) {
    return {
      ids: [],
      entities: {}
    };
  }

  return {
    ids: entities.map(entity => entity[key]),
    entities: _.keyBy(entities, key)
  };
};

export const denormalize = (normalized) => {
  if (!(normalized && Array.isArray(normalized.ids))) {
    return [];
  }

  return normalized.ids.map(id => normalized.entities[id]);
};

export const deleteEntityById = (state, entityId) => {
  const { [entityId]: deleted, ...rest } = state.entities;
  return {
    ...state,
    ids: state.ids.filter(id => id !== entityId),
    entities: rest,
  };
};

export const updateEntity = (state, entity, entityId = null) => {
  entityId = entityId || entity.id;
  if (!state.ids.includes(entityId)) {
    return state;
  }

  return {
    ...state,
    ids: state.ids,
    entities: {
      ...state.entities,
      [entityId]: {
        ...entity
      }
    },
  };
};

export const updateEntities = (state, entities, entityIdKey = 'id') => entities.reduce((acc, curr) => {
  const entityId = curr[entityIdKey];
  if (!acc.ids.includes(entityId)) {
    return acc;
  }

  acc.entities = {
    ...acc.entities,
    [entityId]: {
      ...curr
    }
  };

  return acc;
}, state);

export const upsertEntity = (state, entity, entityId = null, insertAsFirst = false, mergeEntities = false) => {
  entityId = entityId || entity.id;
  return {
    ...state,
    ids: state.ids.includes(entityId) ? state.ids : insertAsFirst ? [entityId, ...state.ids] : [...state.ids, entityId],
    entities: {
      ...state.entities,
      [entityId]: {
        ...(mergeEntities ? state.entities[entityId] || {} : {}),
        ...entity
      }
    },
  };
};

export const upsertEntities = (state, entities, entityIdKey = 'id') => entities.reduce((acc, curr) => {
  const entityId = curr[entityIdKey];

  acc.ids = acc.ids.includes(entityId) ? acc.ids : [...acc.ids, entityId];
  acc.entities = {
    ...acc.entities,
    [entityId]: {
      ...curr
    }
  };

  return acc;
}, state);

export const updateEntityAttribute = (state, entityId, attributeKey, value) => {
  if (!state.ids.includes(entityId)) {
    return state;
  }

  return {
    ...state,
    ids: state.ids,
    entities: {
      ...state.entities,
      [entityId]: {
        ...state.entities[entityId],
        [attributeKey]: value
      }
    }
  };
};

export const updateEntityAttributes = (state, entityId, data) => {
  if (!state.ids.includes(entityId)) {
    return state;
  }

  return {
    ...state,
    ids: state.ids,
    entities: {
      ...state.entities,
      [entityId]: {
        ...state.entities[entityId],
        ...data
      }
    }
  };
};

export const mergeEntities = (state, sourceEntity, destinationEntity, entityKeyProp = 'id', stateKeyProp = 'id', pickFields = []) => {
  if (!(sourceEntity && sourceEntity[entityKeyProp] && destinationEntity && destinationEntity[entityKeyProp])) {
    return state;
  }

  return {
    ids: state.ids.filter(id => id !== destinationEntity[entityKeyProp]).map((id) => {
      if (id === sourceEntity[entityKeyProp]) {
        return destinationEntity[entityKeyProp];
      }

      return id;
    }),
    entities: _.keyBy(
      state.ids.map(id => state.entities[id])
        .filter(entity => entity[stateKeyProp] !== destinationEntity[entityKeyProp])
        .map((entity) => {
          if (entity[stateKeyProp] === sourceEntity[entityKeyProp]) {
            const fields = pickFields.length > 0 ? _.pick(destinationEntity, pickFields) : destinationEntity;
            return {
              ...entity,
              ...fields,
              [stateKeyProp]: destinationEntity[entityKeyProp]
            };
          }

          return entity;
        }), stateKeyProp
    )
  };
};

export const normalizeUserProfile = profile => ({
  ...profile,
  // homeoffice: profile.homeoffice !== null ? profile.homeoffice.toString() : '',
  // remoteWork: profile.remoteWork !== null ? profile.remoteWork : '',
  fields: normalizeById(profile.fields, 'fieldId'),
  roles: normalizeById(profile.roles, 'roleId'),
  skills: normalizeById(profile.skills, 'skillId'),
  ignoredSkills: {
    ...normalizeById(profile.ignoredSkills, 'skillId'),
    slugsMap: profile.ignoredSkills.reduce((acc, curr) => {
      acc[curr.slug] = true;
      return acc;
    }, {})
  },
  languages: normalizeById(profile.languages, 'languageId'),
  works: normalizeById(profile.works, 'id'),
  educations: normalizeById(profile.educations, 'id'),
  attachments: normalizeById(profile.attachments, 'id'),
  links: normalizeById(profile.links, 'id'),
  locations: normalizeById(profile.locations, 'id'),
  employmentTypes: (profile.employmentTypes || []).reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {})
});

export const normalizeSpecializationProfile = profile => ({
  ...profile,
  // homeoffice: profile.homeoffice !== null ? profile.homeoffice.toString() : '',
  // remoteWork: profile.remoteWork !== null ? profile.remoteWork : '',
  roles: normalizeById(profile.roles, 'roleId'),
  skills: normalizeById(profile.skills, 'skillId'),
  ignoredSkills: {
    ...normalizeById(profile.ignoredSkills, 'skillId'),
    slugsMap: profile.ignoredSkills.reduce((acc, curr) => {
      acc[curr.slug] = true;
      return acc;
    }, {})
  },
  languages: normalizeById(profile.languages, 'languageId'),
  locations: normalizeById(profile.locations, 'id'),
  employmentTypes: (profile.employmentTypes || []).reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {})
});

export default normalizeById;
